import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Exchange rates (can be updated manually or through API)
const EXCHANGE_RATES = {
  USD: { INR: 83.15, USD: 1 },
  INR: { USD: 0.012, INR: 1 }
};

// Currency symbols and formatting
const CURRENCY_CONFIG = {
  USD: { symbol: '$', code: 'USD', name: 'US Dollar' },
  INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee' }
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState(EXCHANGE_RATES);

  // Load currency preference and exchange rates from localStorage
  useEffect(() => {
    const savedCurrency = localStorage.getItem('selectedCurrency');
    if (savedCurrency && CURRENCY_CONFIG[savedCurrency]) {
      setSelectedCurrency(savedCurrency);
    }
    
    const savedRates = localStorage.getItem('exchangeRates');
    if (savedRates) {
      try {
        const parsedRates = JSON.parse(savedRates);
        setExchangeRates({ ...EXCHANGE_RATES, ...parsedRates });
      } catch (error) {
        console.warn('Failed to parse saved exchange rates:', error);
      }
    }
  }, []);

  // Save currency preference and exchange rates to localStorage
  useEffect(() => {
    localStorage.setItem('selectedCurrency', selectedCurrency);
  }, [selectedCurrency]);

  useEffect(() => {
    localStorage.setItem('exchangeRates', JSON.stringify(exchangeRates));
  }, [exchangeRates]);

  const convertCurrency = (amount, fromCurrency, toCurrency = selectedCurrency) => {
    if (!amount || amount === 0) return 0;
    if (fromCurrency === toCurrency) return amount;
    
    const rate = exchangeRates[fromCurrency]?.[toCurrency];
    if (!rate) {
      console.warn(`Exchange rate not found for ${fromCurrency} to ${toCurrency}`);
      return amount;
    }
    
    return amount * rate;
  };

  const formatCurrency = (amount, currency = selectedCurrency, options = {}) => {
    const config = CURRENCY_CONFIG[currency];
    if (!config) return `${amount}`;
    
    const convertedAmount = convertCurrency(amount, 'USD', currency);
    
    // Default formatting options
    const formatOptions = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: currency === 'INR' ? 0 : 2,
      ...options
    };

    try {
      return new Intl.NumberFormat('en-US', formatOptions).format(convertedAmount);
    } catch (error) {
      // Fallback formatting
      const formattedAmount = convertedAmount.toLocaleString('en-US', {
        minimumFractionDigits: formatOptions.minimumFractionDigits,
        maximumFractionDigits: formatOptions.maximumFractionDigits
      });
      return `${config.symbol}${formattedAmount}`;
    }
  };

  const updateExchangeRate = (fromCurrency, toCurrency, rate) => {
    setExchangeRates(prev => ({
      ...prev,
      [fromCurrency]: {
        ...prev[fromCurrency],
        [toCurrency]: rate
      }
    }));
    
    // Update inverse rate
    if (rate > 0) {
      setExchangeRates(prev => ({
        ...prev,
        [toCurrency]: {
          ...prev[toCurrency],
          [fromCurrency]: 1 / rate
        }
      }));
    }
  };

  const getCurrencySymbol = (currency = selectedCurrency) => {
    return CURRENCY_CONFIG[currency]?.symbol || '$';
  };

  const getCurrencyCode = (currency = selectedCurrency) => {
    return CURRENCY_CONFIG[currency]?.code || 'USD';
  };

  const getAvailableCurrencies = () => {
    return Object.keys(CURRENCY_CONFIG).map(code => ({
      code,
      ...CURRENCY_CONFIG[code]
    }));
  };

  const value = {
    selectedCurrency,
    setSelectedCurrency,
    exchangeRates,
    convertCurrency,
    formatCurrency,
    updateExchangeRate,
    getCurrencySymbol,
    getCurrencyCode,
    getAvailableCurrencies,
    currencyConfig: CURRENCY_CONFIG
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
